<template>
  <section
    class="sec sec--centered reveal"
    id="faq"
    data-ga-section="faq"
  >
    <div class="sec-head-row sec-head-row--centered">
      <span class="sec-eyebrow">{{ trans.get('landing.faq.eyebrow') }}</span>
      <h2 class="sec-title sec-title--centered">
        {{ trans.get('landing.faq.title_prefix')
        }}<span class="grad">{{ trans.get('landing.faq.title_grad') }}</span
        ><span class="title-dot">{{ trans.get('landing.faq.title_dot') }}</span>
      </h2>
      <p class="sec-lede sec-lede--centered">{{ trans.get('landing.faq.lede') }}</p>
    </div>

    <div class="faq-grid">
      <details
        v-for="question in displayedQuestions"
        :key="question.id"
        class="faq-item"
        data-ga-faq
        @click="toggleFaq"
      >
        <summary>{{ question.question }}</summary>
        <div
          class="faq-answer"
          v-html="question.answer"
        ></div>
      </details>
    </div>

    <div class="faq-more">
      <a
        href="/faq"
        class="more-link"
        >{{ trans.get('landing.faq.more_link') }}</a
      >
    </div>
  </section>
</template>

<script>
import { trackEvent } from '../../../analytics/ga4';

export default {
  name: 'FaqSection',
  data() {
    return {
      faqCategories: [],
    };
  },
  computed: {
    // Teaser on the home page: flatten questions across categories and show
    // the first 6, then link to the full FAQ page.
    displayedQuestions() {
      return this.faqCategories.flatMap((category) => category.questions).slice(0, 6);
    },
  },
  mounted() {
    this.fetchFaq();
  },
  methods: {
    fetchFaq() {
      axios.get(zRoute('ajax.faq_categories.index')).then((response) => {
        this.faqCategories = response.data.data;
      });
    },
    // Toggle the <details> from a click anywhere in the item, not just the
    // summary. Clicks on links inside the answer are left to behave normally.
    toggleFaq(event) {
      if (event.target.closest('a')) return;
      event.preventDefault();
      const details = event.currentTarget;
      const willOpen = !details.open;
      details.open = willOpen;
      if (willOpen) {
        const summary = details.querySelector('summary');
        const question = ((summary && summary.textContent) || '').replace(/\s+/g, ' ').trim().slice(0, 100);
        if (question) {
          trackEvent('faq_open', { faq_question: question });
        }
      }
    },
  },
};
</script>
<style scoped lang="scss">
.faq-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 40px;
  align-items: start;
}

.faq-item {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 18px;
  padding: 22px 26px;
  cursor: pointer;
  transition: border-color 0.2s ease;
  align-self: start;
  user-select: none;
}

.faq-item:hover {
  border-color: rgba(198, 62, 124, 0.3);
}

.faq-item summary {
  font-family: var(--display);
  font-weight: 700;
  font-size: 17px;
  letter-spacing: -0.01em;
  list-style: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  line-height: normal;
}

.faq-item summary::-webkit-details-marker {
  display: none;
}

.faq-item summary::after {
  content: '+';
  font-size: 24px;
  font-weight: 400;
  color: var(--pink);
  transition: transform 0.25s ease;
  font-family: var(--sans);
}

.faq-item[open] summary::after {
  transform: rotate(45deg);
}

.faq-item p,
.faq-answer {
  margin: 14px 0 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ink-dim);
}

.faq-answer p {
  margin: 0 0 10px;
}

.faq-answer p:last-child {
  margin-bottom: 0;
}

.faq-item p strong,
.faq-answer strong {
  color: var(--ink);
}

.faq-answer a {
  color: var(--pink);
  font-weight: 600;
}

.faq-item h6 {
  margin: 14px 0 6px;
  font-family: var(--sans);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--pink);
}

.faq-item ol {
  margin: 6px 0 0;
  padding-left: 0;
  list-style: none;
  counter-reset: s;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.faq-item ol li {
  position: relative;
  padding: 3px 0 3px 30px;
  counter-increment: s;
  font-size: 13.5px;
  color: var(--ink-dim);
  line-height: 1.5;
}

.faq-item ol li::before {
  content: counter(s);
  position: absolute;
  left: 0;
  top: 2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--coral), var(--pink));
  color: #fff;
  font-family: var(--display);
  font-weight: 700;
  font-size: 11px;
  display: grid;
  place-items: center;
  line-height: 1;
}

.faq-more {
  text-align: center;
  margin: 32px 0 0;
}

@media (max-width: 1100px) {
  .faq-grid {
    grid-template-columns: 1fr;
  }
}
</style>
